import { motion } from 'framer-motion';
import { X, Calendar, FileText, ExternalLink, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect } from 'react';
import BLOCKS from '../data/blocks';
import ELEMENTS from '../data/elements';

export default function ElementModal({ element, onClose, onNavigate }) {
  const block = BLOCKS[element.block];
  const isSpeculative = element.block === 'future';

  // Find current element index and navigation availability
  const currentIndex = ELEMENTS.findIndex(el => el.symbol === element.symbol);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < ELEMENTS.length - 1;

  const handlePrevious = () => {
    if (hasPrevious) {
      onNavigate(ELEMENTS[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      onNavigate(ELEMENTS[currentIndex + 1]);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (hasPrevious) {
          onNavigate(ELEMENTS[currentIndex - 1]);
        }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (hasNext) {
          onNavigate(ELEMENTS[currentIndex + 1]);
        }
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, hasPrevious, hasNext, onNavigate, onClose]);

  return (
    <>
      {/* Backdrop */}
      <motion.div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        className="fixed w-[calc(100%-2rem)] max-w-lg z-50"
        style={{ 
          top: '50%', 
          left: '50%', 
          x: '-50%', 
          y: '-50%' 
        }}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      >
        <div
          className="h-auto max-h-[90vh] bg-dark-900 rounded-2xl overflow-hidden border-2 flex flex-col"
          style={{ borderColor: `${block.color}40` }}
        >
          {/* Header */}
          <div
            className="relative p-6"
            style={{ backgroundColor: `${block.color}15` }}
          >
            {/* Navigation and Close Buttons */}
            <div className="absolute top-4 right-4 flex items-center gap-2">
              <button
                onClick={handlePrevious}
                disabled={!hasPrevious}
                className="p-2 rounded-lg bg-black/20 hover:bg-black/40 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Previous element (←)"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={handleNext}
                disabled={!hasNext}
                className="p-2 rounded-lg bg-black/20 hover:bg-black/40 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Next element (→)"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 rounded-lg bg-black/20 hover:bg-black/40 transition-colors"
                title="Close (Esc)"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Element Info */}
            <div className="flex items-start gap-6">
              {/* Symbol Card */}
              <div
                className="w-24 h-28 rounded-xl flex flex-col items-center justify-center border-2"
                style={{
                  backgroundColor: `${block.color}20`,
                  borderColor: block.color,
                  borderStyle: isSpeculative ? 'dashed' : 'solid',
                }}
              >
                <span
                  className="text-3xl font-bold"
                  style={{ color: block.color }}
                >
                  {element.symbol}
                </span>
                <span className="text-xs text-gray-400 mt-1">{element.name}</span>
              </div>

              {/* Details */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="text-xs font-mono px-2 py-0.5 rounded"
                    style={{ backgroundColor: block.color, color: '#000' }}
                  >
                    {block.code}
                  </span>
                  <span className="text-sm text-gray-400">{block.name}</span>
                </div>
                <h2 className="text-2xl font-bold mb-2">{element.name}</h2>
                {isSpeculative && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-500">
                    <Sparkles className="w-3 h-3" />
                    {element.status || 'Speculative'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6 overflow-y-auto flex-1">
            {/* Description */}
            <div>
              <h3 className="text-sm font-semibold text-gray-400 mb-2">Description</h3>
              <p className="text-gray-300 leading-relaxed">{element.description}</p>
            </div>

            {/* Year */}
            {element.year && (
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-500" />
                <div>
                  <span className="text-sm text-gray-400">Introduced</span>
                  <span className="ml-2 text-white font-medium">{element.year}</span>
                </div>
              </div>
            )}

            {/* Key Paper */}
            {element.keyPaper && (
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-gray-500 mt-0.5" />
                <div>
                  <span className="text-sm text-gray-400 block mb-1">Key Reference</span>
                  <span className="text-gray-300 text-sm">{element.keyPaper}</span>
                </div>
              </div>
            )}

            {/* Block Info */}
            <div
              className="p-4 rounded-xl"
              style={{ backgroundColor: `${block.color}10` }}
            >
              <h3 className="text-sm font-semibold mb-2" style={{ color: block.color }}>
                About the {block.name} Block
              </h3>
              <p className="text-sm text-gray-400">{block.longDescription}</p>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-white/5 flex justify-between items-center">
            <span className="text-sm text-gray-500">
              Block {block.number} of 11
            </span>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </>
  );
}
